// El Pulso de un periodo de días cerrados (Pau 08-10-2026: «filtrar por fechas mucho más personalizado» y «ver una
// gráfica de fans únicos chateados»; maqueta «la 3 con cara de Apple»). Primero las palabras (frase y lectura),
// después la cifra, las 6 métricas con su tendencia, las ventas por día con lo normal y los momentos raros, las
// métricas por día, los fans (de cada día y distintos del periodo) y la tabla de cuentas del periodo.
// Un solo día se ve por horas, como «Hoy», frente al día anterior y al de la semana pasada.
import { ArrowDown, ArrowUp, Info } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { t, type TKey } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Aviso, PistaAyuda } from '../ui';
import { diaConSemana, diaCorto, mesLargo, rotulo, type Seleccion } from './fechas';
import { delta, dinero, EN_PUNTOS, miles, numero, SIN_VALOR, SUBIR_ES_BUENO, valorMetrica, type Delta } from './formato';
import { GraficoAcumulado } from './GraficoAcumulado';
import { BarrasDia, BarrasPeriodo, HoverDia, LineaDia, Tendencia } from './GraficosDia';
import { CajaLectura, fraseRango, nombreSerie } from './Lectura';
import { MiniHora } from './MiniHora';
import type { ClaveMetrica, CuentaRangoSala, HorasDiaSala, MomentoSala, RangoSala } from './tipos';

const METRICAS: ClaveMetrica[] = ['unlock', 'cvr', 'golden', 'reply', 'por_fan', 'por_msg'];
const TONO: Record<Delta['tono'], string> = { bueno: 'text-oficina-trabajando', malo: 'text-oficina-error', neutro: 'text-oficina-texto3' };

function Cambio({ d }: { d: Delta | null }) {
  if (!d) return <span className="text-oficina-texto3">{SIN_VALOR}</span>;
  if (d.direccion === 'igual') return <span className="text-oficina-texto3">{t('salaControl.igual')}</span>;
  return <span className={TONO[d.tono]}>{`${d.direccion === 'sube' ? '↑' : '↓'} ${d.texto}`}</span>;
}

function Caja({ titulo, sub, derecha, children, className }: { titulo: string; sub?: string; derecha?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('flex min-w-0 flex-col rounded-o-m bg-oficina-elevada px-4 pb-3 pt-3', className)}>
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-o-texto font-semibold">{titulo}</h3>
          {sub ? <p className="text-o-nota text-oficina-texto3">{sub}</p> : null}
        </div>
        {derecha}
      </div>
      {children}
    </section>
  );
}

/** Rótulo de un periodo de comparación. */
const periodoTexto = (p: { desde: string; hasta: string } | null) => (p ? rotulo(p.desde, p.hasta) : '');

export function textoComparacion(r: RangoSala): string {
  const c = r.comparacion;
  switch (c.tipo) {
    case 'dia':
      return t('salaControl.fechas.comparado.dia');
    case 'mes_anterior':
      return t(c.porDia ? 'salaControl.fechas.comparado.mes_anterior' : 'salaControl.fechas.comparado.mes_anteriorIgual', { mes: c.a ? mesLargo(c.a.desde) : '' });
    case 'inicio_final':
      return t('salaControl.fechas.comparado.inicio_final', { a: periodoTexto(c.a), b: periodoTexto(c.b) });
    case 'anterior':
      return t('salaControl.fechas.comparado.anterior', { n: r.dias, a: periodoTexto(c.a) });
    case 'ninguna':
      return t('salaControl.fechas.comparado.ninguna');
  }
}

/** Una tarjeta de métrica del periodo: el valor del periodo entero, A → B y su tendencia por semana (o por día). */
function TarjetaRango({ clave, r }: { clave: ClaveMetrica; r: RangoSala }) {
  const valor = r.total.metricas[clave];
  const a = r.a?.metricas[clave] ?? null;
  const b = r.b.metricas[clave];
  const unDia = r.dias === 1;
  const sem = r.semana?.metricas[clave] ?? null;
  const tendencia = useMemo(() => {
    const v = r.serie.map((s) => (clave === 'por_fan' ? s.por_fan : s[clave]));
    if (v.length <= 31) return { valores: v, rotulos: r.serie.map((s) => diaConSemana(s.dia)) };
    // Más de un mes: media por semana, para que la línea se lea.
    const semanas: Array<number | null> = [];
    const rotulos: string[] = [];
    for (let i = 0; i < v.length; i += 7) {
      const x = v.slice(i, i + 7).filter((n): n is number => n !== null);
      semanas.push(x.length ? x.reduce((s, n) => s + n, 0) / x.length : null);
      rotulos.push(t('salaControl.grafica.semanaDe', { dia: diaCorto(r.serie[i]!.dia) }));
    }
    return { valores: semanas, rotulos };
  }, [r.serie, clave]);
  const sub =
    clave === 'por_fan' && !unDia
      ? t('salaControl.rango.porFanDistinto')
      : t(`salaControl.metricas.${clave}.ayuda` as TKey);
  return (
    <div className="flex flex-col rounded-o-m bg-oficina-elevada px-3.5 pb-2.5 pt-3">
      <div className="flex items-center gap-1.5 whitespace-nowrap text-o-sub text-oficina-texto2">
        {t(`salaControl.metricas.${clave}.nombre` as TKey)}
        <PistaAyuda texto={t(`salaControl.metricas.${clave}.ayuda` as TKey)}>
          <button type="button" aria-label={t(`salaControl.metricas.${clave}.ayuda` as TKey)} className="inline-flex h-4 items-center text-oficina-texto3 hover:text-oficina-texto">
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        </PistaAyuda>
      </div>
      <p className="truncate text-o-mini text-oficina-texto3">{sub}</p>
      <p className="my-1.5 text-o-titulo font-semibold tracking-tight">{valorMetrica(clave, valor)}</p>
      {unDia ? (
        <p className="text-o-nota leading-relaxed text-oficina-texto3">
          {t('salaControl.ayer')} <Cambio d={delta(valor, a, EN_PUNTOS[clave], SUBIR_ES_BUENO[clave])} />
          <br />
          {t('salaControl.semana')} <Cambio d={delta(valor, sem, EN_PUNTOS[clave], SUBIR_ES_BUENO[clave])} />
        </p>
      ) : r.a ? (
        <p className="text-o-nota leading-relaxed text-oficina-texto3">
          {r.comparacion.tipo === 'inicio_final' ? t('salaControl.rango.tarjetaInicioFinal') : t('salaControl.rango.tarjetaAnterior')}{' '}
          {`${valorMetrica(clave, a)} → ${valorMetrica(clave, b)}`} <Cambio d={delta(b, a, EN_PUNTOS[clave], SUBIR_ES_BUENO[clave])} />
        </p>
      ) : null}
      {!unDia ? <Tendencia valores={tendencia.valores} rotulos={tendencia.rotulos} formato={(v) => valorMetrica(clave, v)} className="mt-auto pt-2" /> : null}
    </div>
  );
}

function Momentos({ momentos, alAbrirDia }: { momentos: MomentoSala[]; alAbrirDia: (d: string) => void }) {
  const [todos, setTodos] = useState(false);
  const visibles = todos ? momentos : momentos.slice(0, 5);
  if (!momentos.length) return <p className="py-6 text-center text-o-sub text-oficina-texto3">{t('salaControl.rango.sinMomentos')}</p>;
  return (
    <ol className="space-y-2">
      {visibles.map((m, i) => (
        <li key={`${m.desde}${m.hasta}`}>
          <button
            type="button"
            onClick={() => alAbrirDia(m.cifras[0]?.dia ?? m.desde)}
            className="w-full rounded-o-s bg-oficina-panel px-3 py-2 text-left outline-none hover:bg-oficina-hover focus-visible:ring-2 focus-visible:ring-oficina-acento"
          >
            <div className="flex items-baseline gap-2">
              <span className={cn('inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-o-mini font-semibold', m.bueno ? 'bg-oficina-trabajando text-oficina-acento-tinta' : 'bg-oficina-error text-oficina-acento-tinta')}>
                {i + 1}
              </span>
              <span className={cn('text-o-nota font-semibold', m.bueno ? 'text-oficina-trabajando' : 'text-oficina-error')}>
                {m.desde === m.hasta ? diaConSemana(m.desde) : rotulo(m.desde, m.hasta)}
              </span>
              {m.cambioNivel ? <span className="text-o-mini text-oficina-texto3">{t('salaControl.rango.cambioNivel')}</span> : null}
              {m.bueno ? <span className="text-o-mini text-oficina-trabajando">{t('salaControl.rango.paraBien')}</span> : null}
            </div>
            <ul className="mt-1 space-y-0.5 pl-7 text-o-nota text-oficina-texto2">
              {m.cifras.slice(0, 3).map((c) => (
                <li key={c.clave}>
                  {`${nombreSerie(c.clave)} `}
                  <b className={cn('font-semibold', c.bueno === false ? 'text-oficina-error' : c.bueno ? 'text-oficina-trabajando' : 'text-oficina-texto')}>{valorSerie(c.clave, c.valor)}</b>
                  {` ${diaCorto(c.dia)} · ${t('salaControl.rango.loNormal', { bajo: valorSerie(c.clave, c.bajo), alto: valorSerie(c.clave, c.alto) })}`}
                </li>
              ))}
            </ul>
          </button>
        </li>
      ))}
      {momentos.length > 5 ? (
        <li>
          <button type="button" onClick={() => setTodos((x) => !x)} className="text-o-nota text-oficina-enlace hover:underline">
            {todos ? t('salaControl.rango.verMenos') : t('salaControl.rango.verMas', { n: momentos.length - 5 })}
          </button>
        </li>
      ) : null}
    </ol>
  );
}

export function valorSerie(clave: string, v: number): string {
  switch (clave) {
    case 'ventas':
      return dinero(v);
    case 'fans':
    case 'msgs':
      return numero(v);
    default:
      return valorMetrica(clave as ClaveMetrica, v);
  }
}

const kilo = (v: number) => (v >= 1_000_000 ? `${numero(v / 1_000_000, 1)} M` : v >= 1000 ? `${miles(v)} k` : numero(v));

type Columna = 'cuenta' | 'ventas' | 'mediaDia' | 'progreso' | 'por_msg' | 'por_fan' | 'unlock' | 'golden' | 'fans' | 'cvr' | 'reply';
const NUMERICAS: Array<Exclude<Columna, 'cuenta'>> = ['ventas', 'mediaDia', 'progreso', 'por_msg', 'por_fan', 'unlock', 'golden', 'fans', 'cvr', 'reply'];
const FORMATO: Record<Exclude<Columna, 'cuenta'>, (v: number) => string> = {
  ventas: (v) => dinero(v),
  mediaDia: (v) => dinero(v),
  progreso: (v) => `${v > 0 ? '↑' : v < 0 ? '↓' : ''} ${numero(Math.abs(v))} %`,
  por_msg: (v) => dinero(v, 3),
  por_fan: (v) => dinero(v, 2),
  unlock: (v) => `${numero(v, 1)} %`,
  golden: (v) => `${numero(v, 1)} %`,
  fans: (v) => numero(v),
  cvr: (v) => `${numero(v, 1)} %`,
  reply: (v) => `${numero(v)} s`,
};
const CABECERA: Record<Columna, TKey> = {
  cuenta: 'salaControl.tabla.cuenta',
  ventas: 'salaControl.tabla.ventas',
  mediaDia: 'salaControl.rango.alDiaCol',
  progreso: 'salaControl.rango.progreso',
  por_msg: 'salaControl.tabla.porMsg',
  por_fan: 'salaControl.tabla.porFan',
  unlock: 'salaControl.tabla.unlock',
  golden: 'salaControl.tabla.golden',
  fans: 'salaControl.tabla.fans',
  cvr: 'salaControl.tabla.cvr',
  reply: 'salaControl.tabla.reply',
};

function TablaRango({ r }: { r: RangoSala }) {
  const [orden, setOrden] = useState<{ col: Columna; asc: boolean }>({ col: 'ventas', asc: false });
  const nombre = (c: CuentaRangoSala) => r.nombres[c.cuenta]?.nombre ?? r.nombres[c.cuenta]?.usuario ?? c.cuenta.slice(0, 8);
  const conProgreso = r.dias > 1 && r.cuentas.some((c) => c.progreso !== null);
  const columnas = NUMERICAS.filter((c) => (c === 'progreso' ? conProgreso : c === 'mediaDia' ? r.dias > 1 : true));
  const filas = useMemo(() => {
    const copia = [...r.cuentas];
    if (orden.col === 'cuenta') return copia.sort((a, b) => nombre(a).localeCompare(nombre(b), 'es') * (orden.asc ? 1 : -1));
    const col = orden.col;
    return copia.sort((a, b) => {
      const va = a[col];
      const vb = b[col];
      if (va === null) return vb === null ? 0 : 1;
      if (vb === null) return -1;
      return orden.asc ? va - vb : vb - va;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r, orden]);
  const cab = (col: Columna, derecha: boolean) => (
    <th key={col} className={cn('sticky top-0 z-[1] bg-oficina-elevada px-2 py-2 font-medium', derecha ? 'text-right' : 'text-left')}>
      <button type="button" onClick={() => setOrden((o) => ({ col, asc: o.col === col ? !o.asc : col === 'cuenta' }))} className={cn('inline-flex items-center gap-1 hover:text-oficina-texto', orden.col === col && 'text-oficina-texto')}>
        {t(CABECERA[col])}
        {orden.col === col ? orden.asc ? <ArrowUp className="h-3 w-3" aria-hidden /> : <ArrowDown className="h-3 w-3" aria-hidden /> : null}
      </button>
    </th>
  );
  return (
    <div className="max-h-[560px] overflow-y-auto">
      <table className="w-full text-o-sub">
        <thead>
          <tr className="text-o-nota text-oficina-texto3">
            {cab('cuenta', false)}
            {columnas.slice(0, conProgreso ? 3 : 2).map((c) => cab(c, true))}
            {r.dias > 1 ? <th className="sticky top-0 z-[1] bg-oficina-elevada px-2 py-2 text-left font-medium">{t('salaControl.rango.tendencia')}</th> : null}
            {columnas.slice(conProgreso ? 3 : 2).map((c) => cab(c, true))}
          </tr>
        </thead>
        <tbody>
          {filas.map((x, i) => (
            <tr key={x.cuenta} className="border-t border-oficina-linea hover:bg-oficina-hover">
              <td className="max-w-[16rem] truncate py-1.5 pl-2 pr-2">
                <span className="mr-2 inline-block w-6 text-right text-o-nota text-oficina-texto3">{i + 1}</span>
                {nombre(x)}
                {r.nombres[x.cuenta]?.usuario && r.nombres[x.cuenta]?.nombre ? <span className="ml-1.5 text-o-nota text-oficina-texto3">{`@${r.nombres[x.cuenta]!.usuario}`}</span> : null}
                {x.nuevaDesde ? <span className="ml-1.5 text-o-mini text-oficina-enlace">{t('salaControl.rango.nueva', { dia: diaCorto(x.nuevaDesde) })}</span> : null}
              </td>
              {columnas.slice(0, conProgreso ? 3 : 2).map((c) => (
                <td key={c} className={cn('px-2 text-right tabular-nums', c === 'ventas' && 'font-semibold', c === 'progreso' && x.progreso !== null && (x.progreso >= 0 ? 'text-oficina-trabajando' : 'text-oficina-error'))}>
                  {x[c] === null ? SIN_VALOR : FORMATO[c](x[c]!)}
                </td>
              ))}
              {r.dias > 1 ? (
                <td className="w-28 px-2">
                  <Tendencia valores={x.tramos} />
                </td>
              ) : null}
              {columnas.slice(conProgreso ? 3 : 2).map((c) => (
                <td key={c} className="px-2 text-right tabular-nums">
                  {x[c] === null ? SIN_VALOR : FORMATO[c](x[c]!)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Acumulado por horas de un día guardado (para la vista de un día). */
function acumuladoDe(h: HorasDiaSala | null): number[] {
  if (!h) return [];
  const puntos = [0];
  for (let i = 0; i < 24; i++) puntos.push(puntos[i]! + (h.neto[i] ?? 0));
  return puntos;
}

function porHora(h: HorasDiaSala | null, f: (h: HorasDiaSala, i: number) => number | null): Array<number | null> {
  return Array.from({ length: 24 }, (_, i) => (h ? f(h, i) : null));
}

function VistaDia({ r }: { r: RangoSala }) {
  const h = r.horas;
  if (!h?.dia) return <p className="text-o-sub text-oficina-texto3">{t('salaControl.rango.sinHoras')}</p>;
  const referencia = new Date(`${r.desde}T12:00:00Z`);
  const pie = diaCorto(r.desde);
  const unlock = (x: HorasDiaSala, i: number) => ((x.ppv_env[i] ?? 0) >= 20 ? (100 * (x.ppv_comp[i] ?? 0)) / x.ppv_env[i]! : null);
  const reply = (x: HorasDiaSala, i: number) => ((x.reply_n[i] ?? 0) >= 20 ? (x.reply_ms[i] ?? 0) / x.reply_n[i]! / 1000 : null);
  return (
    <div className="grid grid-cols-1 gap-2.5 xl:h-[250px] xl:grid-cols-[5fr_7fr]">
      <div className="flex min-h-[220px] flex-col rounded-o-m bg-oficina-elevada px-4 pb-2 pt-3">
        <h3 className="mb-1 text-o-texto font-semibold">{t('salaControl.rango.horasDia')}</h3>
        <div className="min-h-0 flex-1">
          <GraficoAcumulado acumulado={{ hoy: acumuladoDe(h.dia), ayer: acumuladoDe(h.ayer), sem: acumuladoDe(h.sem) }} horaHoy={24} referencia={referencia} etiqueta={t('salaControl.rango.horasDia')} />
        </div>
      </div>
      <div className="grid min-h-[220px] grid-cols-2 grid-rows-2 gap-2.5">
        <MiniHora titulo={t('salaControl.porHora.ventas')} hoy={porHora(h.dia, (x, i) => x.neto[i] ?? 0)} ayer={porHora(h.ayer, (x, i) => x.neto[i] ?? 0)} formato={(x) => dinero(x)} enPuntos={false} referencia={referencia} pie={pie} />
        <MiniHora titulo={t('salaControl.porHora.unlock')} hoy={porHora(h.dia, unlock)} ayer={porHora(h.ayer, unlock)} formato={(x) => `${numero(x, 1)} %`} enPuntos referencia={referencia} pie={pie} />
        <MiniHora titulo={t('salaControl.porHora.pagan')} hoy={porHora(h.dia, (x, i) => x.pagan[i] ?? 0)} ayer={porHora(h.ayer, (x, i) => x.pagan[i] ?? 0)} formato={(x) => numero(x)} enPuntos={false} referencia={referencia} pie={pie} />
        <MiniHora titulo={t('salaControl.porHora.reply')} hoy={porHora(h.dia, reply)} ayer={porHora(h.ayer, reply)} formato={(x) => `${numero(x)} s`} enPuntos={false} subirEsBueno={false} referencia={referencia} pie={pie} />
      </div>
    </div>
  );
}

export function PulsoRango({ r, alElegir }: { r: RangoSala; alElegir: (s: Seleccion) => void }) {
  const dias = r.serie.map((s) => s.dia);
  const abrirDia = (d: string) => alElegir({ atajo: 'fechas', desde: d, hasta: d });
  const unDia = r.dias === 1;
  const ventas = r.lectura.find((p) => p.tipo === 'ventas');
  const fans = r.lectura.find((p) => p.tipo === 'fans');
  const deltaVentas = r.a ? delta(r.b.mediaDia, r.a.mediaDia, false) : null;
  const meses = r.fans.porMes;
  const vueltas = r.fans.distintos > 0 ? r.fans.sumaDias / r.fans.distintos : null;
  return (
    <HoverDia>
      <div className="flex flex-col gap-3">
        {r.faltan.length ? <Aviso tono="aviso">{t('salaControl.fechas.rellenando', { n: r.faltan.length })}</Aviso> : null}
        {r.internasOk === false ? <Aviso tono="aviso">{t('salaControl.fechas.sinInternas')}</Aviso> : null}

        <p className="max-w-[1100px] text-o-titulo2 leading-snug text-oficina-texto">
          {ventas ? fraseRango(ventas) : null} {fans && !unDia ? fraseRango(fans) : null}
        </p>

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[5fr_7fr]">
          <div className="px-1">
            <p className="mb-1.5 text-o-texto text-oficina-texto2">{t('salaControl.rango.ventasDe', { periodo: unDia ? diaConSemana(r.desde) : rotulo(r.desde, r.hasta) })}</p>
            <p className="text-o-cifra-grande font-bold leading-none tracking-tight">{dinero(r.total.ventas)}</p>
            <div className="mt-2 space-y-0.5 text-o-texto text-oficina-texto3">
              {!unDia ? <p>{t('salaControl.rango.mediaDia', { media: dinero(r.total.mediaDia) })}</p> : null}
              {r.comparacion.tipo === 'inicio_final' && r.a ? (
                <p>
                  {t('salaControl.rango.inicioFinal', { a: dinero(r.a.mediaDia), b: dinero(r.b.mediaDia) })} <Cambio d={deltaVentas} />
                </p>
              ) : r.a ? (
                <p>
                  <Cambio d={unDia ? delta(r.total.ventas, r.a.ventas, false) : deltaVentas} />{' '}
                  {unDia ? `${t('salaControl.rango.vsDiaAnterior')} (${dinero(r.a.ventas)})` : t('salaControl.rango.vsComparado', { cambio: '', periodo: periodoTexto(r.comparacion.a), valor: numero(r.a.mediaDia) })}
                </p>
              ) : null}
              {unDia && r.semana ? (
                <p>
                  <Cambio d={delta(r.total.ventas, r.semana.ventas, false)} /> {`${t('salaControl.vsSemana')} (${dinero(r.semana.ventas)})`}
                </p>
              ) : null}
              {r.comparacion.porDia ? <p className="text-o-nota">{t('salaControl.rango.porDiaNota')}</p> : null}
            </div>
          </div>
          <CajaLectura
            titulo={t('salaControl.lectura.tituloRango')}
            derecha={rotulo(r.desde, r.hasta)}
            piezas={r.lectura.map((p) => ({ tono: p.tono, texto: fraseRango(p) }))}
          />
        </div>

        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-6">
          {METRICAS.map((m) => (
            <TarjetaRango key={m} clave={m} r={r} />
          ))}
        </div>

        {unDia ? (
          <VistaDia r={r} />
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2.5 xl:grid-cols-[8fr_4fr]">
              <Caja titulo={t('salaControl.rango.ventasPorDia')} sub={t('salaControl.rango.ventasPorDiaSub')} className="min-h-[300px]">
                <div className="min-h-[240px] flex-1">
                  <BarrasDia
                    dias={dias}
                    valores={r.serie.map((s) => s.ventas)}
                    banda={r.bandas.ventas}
                    formato={(v) => dinero(v)}
                    formatoEje={(v) => `${kilo(v)}$`}
                    etiqueta={t('salaControl.rango.ventasPorDia')}
                    alSeleccionar={abrirDia}
                  />
                </div>
              </Caja>
              <Caja titulo={t('salaControl.rango.momentos')} sub={t('salaControl.rango.momentosSub')}>
                <Momentos momentos={r.momentos} alAbrirDia={abrirDia} />
              </Caja>
            </div>

            <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-4">
              {(['unlock', 'golden', 'reply', 'por_msg'] as const).map((k) => (
                <Caja key={k} titulo={nombreSerie(k)} sub={valorMetrica(k, r.total.metricas[k])} className="h-[170px]">
                  <div className="min-h-0 flex-1">
                    <LineaDia
                      dias={dias}
                      valores={r.serie.map((s) => s[k])}
                      banda={r.bandas[k]}
                      formatoEje={(v) => (k === 'reply' ? `${numero(v, v % 1 ? 1 : 0)} s` : k === 'por_msg' ? `${numero(v, 2)}` : `${numero(v, v % 1 ? 1 : 0)} %`)}
                      formato={(v) => valorMetrica(k, v)}
                      etiqueta={nombreSerie(k)}
                    />
                  </div>
                </Caja>
              ))}
            </div>
          </>
        )}

        <h3 className="mt-2 px-1 text-o-nota font-semibold text-oficina-texto2">{t('salaControl.rango.fansTitulo')}</h3>
        <div className={cn('grid grid-cols-1 gap-2.5', !unDia && 'xl:grid-cols-[8fr_4fr]')}>
          {unDia ? (
            <Caja titulo={t('salaControl.rango.fansDia')} sub={t('salaControl.rango.fansDiaSub')}>
              <p className="text-o-cifra font-bold tracking-tight">{numero(r.total.fans)}</p>
              <p className="text-o-sub text-oficina-texto3">
                {r.a ? <><Cambio d={delta(r.total.fans, r.a.fans, false)} /> {`${t('salaControl.rango.vsDiaAnterior')} (${numero(r.a.fans)})`}</> : null}
                {r.semana ? <><br /><Cambio d={delta(r.total.fans, r.semana.fans, false)} /> {`${t('salaControl.vsSemana')} (${numero(r.semana.fans)})`}</> : null}
              </p>
            </Caja>
          ) : (
            <Caja titulo={t('salaControl.rango.fansDia')} sub={t('salaControl.rango.fansDiaSub')} className="min-h-[280px]">
              <div className="min-h-[220px] flex-1">
                <BarrasDia
                  dias={dias}
                  valores={r.serie.map((s) => s.fans)}
                  banda={r.bandas.fans}
                  formato={(v) => numero(v)}
                  formatoEje={kilo}
                  etiqueta={t('salaControl.rango.fansDia')}
                  conMedia={false}
                  alSeleccionar={abrirDia}
                />
              </div>
            </Caja>
          )}
          {unDia ? null : <Caja titulo={t('salaControl.rango.distintos')} sub={t('salaControl.rango.distintosSub')}>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-o-s bg-oficina-panel px-3 py-2">
                <p className="text-o-nota text-oficina-texto3">{t('salaControl.rango.enElPeriodo')}</p>
                <p className="text-o-titulo font-bold tracking-tight">{numero(r.fans.distintos)}</p>
                {!unDia ? <p className="text-o-mini text-oficina-texto3">{t('salaControl.rango.sumaDias', { n: numero(r.fans.sumaDias) })}</p> : null}
              </div>
              <div className="rounded-o-s bg-oficina-panel px-3 py-2">
                <p className="text-o-nota text-oficina-texto3">{t('salaControl.rango.alDia')}</p>
                <p className="text-o-titulo font-bold tracking-tight">{numero(r.fans.mediaDia)}</p>
                {r.a && !unDia && (r.comparacion.tipo === 'inicio_final' || r.a.dias === r.b.dias) ? (
                  <p className="text-o-mini text-oficina-texto3">
                    {`${numero(r.a.fans)} → ${numero(r.b.fans)} `}
                    <Cambio d={delta(r.b.fans, r.a.fans, false)} />
                  </p>
                ) : null}
              </div>
            </div>
            {vueltas && !unDia ? <p className="mt-2 text-o-nota text-oficina-texto3">{t('salaControl.rango.vueltas', { n: numero(vueltas, 1), d: r.dias })}</p> : null}
            {r.fans.porSemana.length > 1 ? (
              <div className="mt-3 h-[150px]">
                <p className="text-o-nota text-oficina-texto2">{t('salaControl.rango.porSemanaTitulo')}</p>
                <BarrasPeriodo
                  barras={r.fans.porSemana.map((s) => ({ rotulo: diaCorto(s.desde), titulo: t('salaControl.grafica.semanaDe', { dia: diaCorto(s.desde) }), valor: s.distintos, incompleta: !s.completa }))}
                  etiqueta={t('salaControl.rango.porSemanaTitulo')}
                  formatoEje={kilo}
                />
                {r.fans.porSemana.some((s) => !s.completa) ? <p className="text-o-mini text-oficina-texto3">{t('salaControl.rango.rayadas')}</p> : null}
              </div>
            ) : null}
          </Caja>}
        </div>

        {meses.length > 1 ? (
          <Caja titulo={t('salaControl.rango.porMes')} sub={t('salaControl.rango.porMesSub')}>
            <table className="w-full text-o-sub">
              <thead>
                <tr className="text-o-nota text-oficina-texto3">
                  <th className="px-2 py-1.5 text-left font-medium">{t('salaControl.rango.mes')}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t('salaControl.rango.distintos')}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t('salaControl.rango.ventasDia')}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t('salaControl.rango.porFanCol')}</th>
                  <th className="px-2 py-1.5 text-right font-medium">{t('salaControl.rango.conversionCol')}</th>
                </tr>
              </thead>
              <tbody>
                {meses.map((m) => (
                  <tr key={m.mes} className="border-t border-oficina-linea">
                    <td className="px-2 py-1.5">
                      {m.completo ? mesLargo(m.desde) : rotulo(m.desde, m.hasta)}
                      {!m.completo ? <span className="ml-1.5 text-o-mini text-oficina-texto3">{t('salaControl.rango.incompleto')}</span> : null}
                    </td>
                    <td className="px-2 text-right tabular-nums">{numero(m.distintos)}</td>
                    <td className="px-2 text-right tabular-nums">{dinero(m.ventasDia)}</td>
                    <td className="px-2 text-right tabular-nums">{m.porFan === null ? SIN_VALOR : dinero(m.porFan, 2)}</td>
                    <td className="px-2 text-right tabular-nums">{m.cvr === null ? SIN_VALOR : `${numero(m.cvr, 1)} %`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Caja>
        ) : null}

        <Caja titulo={t('salaControl.rango.cuentas')} sub={t('salaControl.rango.cuentasSub')}>
          <TablaRango r={r} />
        </Caja>
      </div>
    </HoverDia>
  );
}
